import type { BulkAssignResult, DistributeResult } from '@performance-crm/shared';

/** Aviso de resultado de una acción masiva: título, detalle y tono (error cuando algo no se pudo, dura más). */
export interface BulkSummary {
  tone: 'success' | 'error' | 'info';
  title: string;
  description?: string;
}

/** «1 000» con espacio como separador de miles (los textos del dueño lo escriben así). */
export function groupThousands(n: number) {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

const leads = (n: number) => `${groupThousands(n)} lead${n === 1 ? '' : 's'}`;
const couldNot = (n: number) => (n === 1 ? '1 no se pudo' : `${groupThousands(n)} no se pudieron`);

/** Motivos de los que fallaron (los cinco primeros con el nombre del lead cuando se conoce) y «y N más». */
function failedDetail(
  failed: ReadonlyArray<{ id: string; reason: string }>,
  nameOf: (id: string) => string | undefined,
) {
  const why = failed
    .slice(0, 5)
    .map((f) => `${nameOf(f.id) ?? 'Lead'}: ${f.reason}`)
    .join(' · ');
  return failed.length > 5 ? `${why} · y ${failed.length - 5} más` : why;
}

/**
 * Resultado de la reasignación en bloque (Lote 4 · G3 y Lote 6 · I4, P20), concordando en número: «1 lead
 * reasignado», «Se reasignaron 36 leads; 4 ya lo tenían», «Se reasignó 1 lead; 2 no se pudieron: …», «Ningún lead
 * cambió: 3 ya lo tenían». Los que ya tenían ese asesor no son un error ni un cambio.
 */
export function bulkAssignSummary(
  r: BulkAssignResult,
  nameOf: (id: string) => string | undefined,
): BulkSummary {
  const unchanged =
    r.unchanged > 0
      ? r.unchanged === 1
        ? '1 ya lo tenía'
        : `${groupThousands(r.unchanged)} ya lo tenían`
      : null;
  if (r.failed.length === 0) {
    if (r.assigned === 0)
      return { tone: 'info', title: `Ningún lead cambió: ${unchanged ?? 'nada que reasignar'}` };
    const done = r.assigned === 1 ? '1 lead reasignado' : `${leads(r.assigned)} reasignados`;
    return unchanged
      ? {
          tone: 'success',
          title: `${r.assigned === 1 ? 'Se reasignó' : 'Se reasignaron'} ${leads(r.assigned)}; ${unchanged}`,
        }
      : { tone: 'success', title: done };
  }
  // Algo no se movió: se dice cuántos sí (y cuántos ya lo tenían) y por qué no los demás.
  const head =
    r.assigned === 0
      ? unchanged
        ? `Ningún lead cambió: ${unchanged}; ${couldNot(r.failed.length)}`
        : r.failed.length === 1
          ? 'No se pudo reasignar el lead'
          : `No se pudo reasignar ninguno de los ${leads(r.failed.length)}`
      : `${r.assigned === 1 ? 'Se reasignó' : 'Se reasignaron'} ${leads(r.assigned)}; ${
          unchanged ? `${unchanged}; ` : ''
        }${couldNot(r.failed.length)}`;
  return { tone: 'error', title: head, description: failedDetail(r.failed, nameOf) };
}

/**
 * Resultado del reparto de la bolsa (Lote 6 · I4, P18): «Se repartieron 1 000 leads», «Se repartieron 95 leads; 5
 * no se pudieron: …» (motivos agrupados con su cantidad) y, si quedan leads en la bolsa, cuántos («Queda 1 lead» /
 * «Quedan 250 leads») y «vuelve a repartir para el resto» cuando volver a repartir sí los tomaría (no cuando
 * quedaron porque nadie puede atender su proyecto). `remaining` null = el servidor no pudo contarlos.
 */
export function distributeSummary(
  r: DistributeResult,
  advisorName: (id: string) => string | undefined,
): BulkSummary {
  const perAdvisor = Object.entries(r.perAdvisor)
    .map(([id, n]) => `${advisorName(id) ?? id}: ${groupThousands(n)}`)
    .join(' · ');
  const details: string[] = [];
  if (perAdvisor) details.push(perAdvisor);
  if (r.failed.length) {
    const grouped = new Map<string, number>();
    for (const f of r.failed) grouped.set(f.reason, (grouped.get(f.reason) ?? 0) + 1);
    details.push(
      [...grouped.entries()]
        .map(([reason, n]) => (n === 1 ? reason : `${reason} (${groupThousands(n)})`))
        .join(' · '),
    );
  }
  if (r.remaining === null)
    details.push('No se pudo contar cuántos quedan sin asignar; revisa la Bolsa.');
  else if (r.remaining > 0)
    details.push(
      `${r.remaining === 1 ? 'Queda' : 'Quedan'} ${leads(r.remaining)} sin asignar${
        r.remaining > r.failed.length ? '; vuelve a repartir para el resto' : ''
      }.`,
    );
  const description = details.length ? details.join(' · ') : undefined;
  if (r.failed.length === 0 && r.assigned === 0)
    return { tone: 'info', title: 'No había leads para repartir', description };
  const done =
    r.assigned === 0
      ? 'No se repartió ningún lead'
      : `${r.assigned === 1 ? 'Se repartió' : 'Se repartieron'} ${leads(r.assigned)}`;
  return r.failed.length
    ? { tone: 'error', title: `${done}; ${couldNot(r.failed.length)}`, description }
    : { tone: 'success', title: done, description };
}
